import { ref } from 'vue';
import { defineStore } from 'pinia';
import { useNuxtApp } from 'nuxt/app';
import { YOUTUBE_IMAGE_URL_PREFIX, YOUTUBE_IMAGE_VARIATIONS } from '~~/common/config/VideoConfig';
import { vimeoMeta } from '~~/common/utils/video';

interface VideoThumbnail {
  url: string;
}

export const useVideoStore = defineStore('video', () => {
  const nuxtApp = useNuxtApp();
  const videoCache = ref<Record<string, VideoThumbnail[]>>({});

  function fetchVideoToCache (id: string): VideoThumbnail[] | Promise<void> {
    return videoCache.value[id] || vimeoMeta({ videoId: id, isCacheEnabled: true, $logger: nuxtApp.$logger }).then(
      ({ thumbnail_url: thumbnailUrl }) => {
        if (thumbnailUrl) {
          videoCache.value[id] = [{
            url: thumbnailUrl,
          }];
        }
      },
    );
  }

  function getVideoFallbackImage (id: string): VideoThumbnail[] {
    return YOUTUBE_IMAGE_VARIATIONS.map((variation) => ({
      url: `${YOUTUBE_IMAGE_URL_PREFIX}${id}${variation}`,
    }));
  }

  function getYoutubeThumbnailSources (id: string): VideoThumbnail[] {
    if (videoCache.value[id]) {
      return videoCache.value[id];
    }
    if (id) {
      fetchVideoToCache(id);
    }
    return getVideoFallbackImage(id);
  }

  async function asyncGetYoutubeThumbnailSources (id: string): Promise<VideoThumbnail[] | undefined> {
    if (videoCache.value[id]) {
      return videoCache.value[id];
    }
    if (id) {
      return await Promise.resolve(fetchVideoToCache(id)).then(() => {
        if (videoCache.value[id]) {
          return videoCache.value[id];
        }
        return getVideoFallbackImage(id);
      });
    }
  }

  return {
    videoCache,
    fetchVideoToCache,
    getVideoFallbackImage,
    getYoutubeThumbnailSources,
    asyncGetYoutubeThumbnailSources,
  };
});
